import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import GoogleTranslate from '../components/common/GoogleTranslate';
import { safeJsonParse } from '../utils/storage';
import './LandingPage.css';

const Icon = ({ name, size = 18 }) => {
  const paths = {
    building: <><path d="M4 21h16" /><path d="M5 10h14" /><path d="M6 10V7l6-4 6 4v3" /><path d="M8 14v4" /><path d="M12 14v4" /><path d="M16 14v4" /></>,
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M10 20v-5h4v5" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,
    globe: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15 15 0 0 1 0 20" /><path d="M12 2a15 15 0 0 0 0 20" /></>,
    file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>,
    phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.12.92.33 1.82.63 2.69a2 2 0 0 1-.45 2.11L8 9.8a16 16 0 0 0 6.2 6.2l1.28-1.28a2 2 0 0 1 2.11-.45c.87.3 1.77.51 2.69.63A2 2 0 0 1 22 16.92z" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    pin: <><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></>,
    chevron: <><path d="m9 18 6-6-6-6" /></>,
    reset: <><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v6h6" /></>,
  };

  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
};

const normalize = value => String(value || '').toLowerCase().trim();

export default function LandingPage() {
  const { tenants, tenantsLoading } = useTenant();
  const { isAuthenticated, user, tenant: authTenant, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [taluka, setTaluka] = useState('all');
  const [district, setDistrict] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    if (authLoading || !isAuthenticated || !user) return;
    const portalType = localStorage.getItem('portalType');
    const savedTenant = authTenant || safeJsonParse(localStorage.getItem('tenant'));

    if (portalType === 'system' || user.role === 'super_admin') {
      navigate('/system/dashboard', { replace: true });
    } else if (portalType === 'taluka' || user.role === 'tahashil_officer' || user.role === 'sdo_officer') {
      navigate('/taluka/dashboard', { replace: true });
    } else if (user.role === 'citizen' && savedTenant?.slug) {
      navigate(`/gp/${savedTenant.slug}`, { replace: true });
    } else if (savedTenant?.slug) {
      navigate(`/admin/${savedTenant.slug}/dashboard`, { replace: true });
    }
  }, [isAuthenticated, user, authTenant, authLoading, navigate]);

  useEffect(() => {
    if (authLoading || tenantsLoading || isAuthenticated) return;
    if (tenants.length === 1 && tenants[0]?.slug) {
      navigate(`/gp/${tenants[0].slug}`, { replace: true });
    }
  }, [authLoading, tenantsLoading, isAuthenticated, tenants, navigate]);

  const talukas = useMemo(() => [...new Set(tenants.map(item => item.taluka).filter(Boolean))].sort(), [tenants]);
  const districts = useMemo(() => [...new Set(tenants.map(item => item.district).filter(Boolean))].sort(), [tenants]);
  const heroTenant = useMemo(() => tenants.find(item => item.banner_url) || tenants.find(item => item.logo_url) || null, [tenants]);

  const filteredTenants = useMemo(() => {
    const query = normalize(search);
    const items = tenants.filter(item => {
      const matchesSearch = !query || [
        item.name,
        item.taluka,
        item.district,
        item.state,
        item.sarpanch_name,
        item.slug,
      ].some(value => normalize(value).includes(query));
      const matchesTaluka = taluka === 'all' || item.taluka === taluka;
      const matchesDistrict = district === 'all' || item.district === district;
      return matchesSearch && matchesTaluka && matchesDistrict;
    });

    return items.sort((a, b) => {
      if (sortBy === 'taluka') return normalize(a.taluka).localeCompare(normalize(b.taluka));
      if (sortBy === 'district') return normalize(a.district).localeCompare(normalize(b.district));
      return normalize(a.name).localeCompare(normalize(b.name));
    });
  }, [tenants, search, taluka, district, sortBy]);

  const resetFilters = () => {
    setSearch('');
    setTaluka('all');
    setDistrict('all');
    setSortBy('name');
  };

  const stats = [
    { icon: 'building', label: 'एकूण ग्रामपंचायती', value: tenants.length || '—', sub: 'या पोर्टलमध्ये' },
    { icon: 'users', label: 'नागरिक सेवा', value: `${tenants.length ? tenants.length * 4 : 0}+`, sub: 'डिजिटल सुविधा' },
    { icon: 'bell', label: 'डिजिटल सूचना', value: tenants.length ? tenants.length : '—', sub: 'ग्रामपंचायत स्तरावर' },
    { icon: 'globe', label: 'डिजिटल प्रवेश', value: tenants.length ? '100%' : '—', sub: 'सार्वजनिक पोर्टल' },
  ];

  const heroStyle = heroTenant?.banner_url
    ? { backgroundImage: `linear-gradient(90deg, rgba(3, 35, 79, .98) 0%, rgba(3, 35, 79, .86) 42%, rgba(3, 35, 79, .18) 100%), url("${heroTenant.banner_url}")` }
    : undefined;

  if (authLoading || tenantsLoading || (!isAuthenticated && tenants.length === 1)) {
    return <div className="landing-empty" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>पोर्टल लोड होत आहे...</div>;
  }

  return (
    <div className="landing-page">
      <header className="landing-shell-header">
        <div className="landing-topbar">
          <div className="landing-container landing-topbar-inner">
            <span><Icon name="building" size={16} /> ई-गव्हर्नन्स सुलभतेसाठी एक खिडकी</span>
            <nav style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <GoogleTranslate />
              <a href="#gp-search">ग्रामपंचायत शोधा</a>
              <Link to="/login">लॉगिन</Link>
            </nav>
          </div>
        </div>

        <div className="landing-mainnav">
          <div className="landing-container landing-mainnav-inner">
            <Link className="landing-brand" to="/">
              <span className="landing-emblem"><Icon name="building" size={34} /></span>
              <span>
                <strong>डिजिटल ग्रामपंचायत पोर्टल</strong>
                <small>दौंड तालुका, जि. पुणे, महाराष्ट्र</small>
              </span>
            </Link>
            <nav className="landing-navlinks">
              <a className="active" href="#top"><Icon name="home" size={15} /> मुख्यपृष्ठ</a>
              <a href="#gp-search"><Icon name="search" size={15} /> शोधा</a>
              <a href="#gp-list"><Icon name="file" size={15} /> ग्रामपंचायती</a>
              <Link to="/login"><Icon name="users" size={15} /> माझे खाते</Link>
            </nav>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="landing-hero" style={heroStyle}>
          <div className="landing-container landing-hero-inner">
            <div className="landing-hero-copy">
              <h1>डिजिटल ग्रामपंचायत पोर्टल (दौंड तालुका)</h1>
              <p>दौंड तालुक्यातील सर्व ग्रामपंचायतींच्या पोर्टलमध्ये आपले स्वागत आहे. ग्रामपंचायत शोधा, माहिती मिळवा आणि नागरिक सेवा जलद वापरा.</p>
            </div>
          </div>
        </section>

        <section className="landing-container landing-stat-strip" aria-label="पोर्टल माहिती">
          {stats.map(item => (
            <article key={item.label}>
              <span><Icon name={item.icon} size={26} /></span>
              <div>
                <strong>{item.value}</strong>
                <b>{item.label}</b>
                <small>{item.sub}</small>
              </div>
            </article>
          ))}
        </section>

        <section className="landing-container landing-search-panel" id="gp-search">
          <div>
            <h2>ग्रामपंचायत शोधा</h2>
            <p>आपल्या ग्रामपंचायतीचा पोर्टल शोधण्यासाठी खालील शोध आणि फिल्टर वापरा.</p>
          </div>
          <div className="landing-search-controls">
            <label className="landing-search-input">
              <Icon name="search" size={18} />
              <input
                type="text"
                placeholder="ग्रामपंचायत शोधा - नाव, गाव, तालुका, सरपंच..."
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
            </label>
            <select value={taluka} onChange={event => setTaluka(event.target.value)} aria-label="तालुका निवडा">
              <option value="all">सर्व तालुका</option>
              {talukas.map(item => <option key={item} value={item}>{item}</option>)}
            </select>
            <select value={district} onChange={event => setDistrict(event.target.value)} aria-label="जिल्हा निवडा">
              <option value="all">सर्व जिल्हे</option>
              {districts.map(item => <option key={item} value={item}>{item}</option>)}
            </select>
            <button type="button" className="landing-search-button">
              <Icon name="search" size={16} /> शोधा
            </button>
            <button type="button" className="landing-reset-button" onClick={resetFilters} aria-label="फिल्टर रीसेट करा">
              <Icon name="reset" size={16} />
            </button>
          </div>
        </section>

        <section className="landing-container landing-results-head" id="gp-list">
          <div>
            <h2>सर्व ग्रामपंचायत <span>{filteredTenants.length}</span></h2>
            <p>{tenants.length ? `${tenants.length} पैकी ${filteredTenants.length} ग्रामपंचायती दाखवत आहे` : 'ग्रामपंचायती लोड होत आहेत'}</p>
          </div>
          <label>
            क्रम लावा:
            <select value={sortBy} onChange={event => setSortBy(event.target.value)}>
              <option value="name">नावानुसार (अ-ह)</option>
              <option value="taluka">तालुक्यानुसार</option>
              <option value="district">जिल्ह्यानुसार</option>
            </select>
          </label>
        </section>

        <section className="landing-container">
          {!tenants.length ? (
            <div className="landing-empty">ग्रामपंचायती लोड होत आहेत...</div>
          ) : !filteredTenants.length ? (
            <div className="landing-empty">तुमच्या शोधाशी जुळणारी ग्रामपंचायत सापडली नाही.</div>
          ) : (
            <div className="landing-gp-grid">
              {filteredTenants.map(tenant => (
                <Link key={tenant.id} to={`/gp/${tenant.slug}`} className="landing-gp-card">
                  <span className="landing-gp-logo">
                    {tenant.logo_url ? <img src={tenant.logo_url} alt="" /> : <Icon name="home" size={28} />}
                  </span>
                  <span className="landing-gp-copy">
                    <strong>{tenant.name}</strong>
                    <small>{[tenant.taluka, tenant.district, tenant.state].filter(Boolean).join(', ') || 'महाराष्ट्र'}</small>
                    {tenant.sarpanch_name ? <em>सरपंच: {tenant.sarpanch_name}</em> : <em>सरपंच माहिती उपलब्ध नाही</em>}
                  </span>
                  <span className="landing-gp-action">पोर्टल पहा <Icon name="chevron" size={14} /></span>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="landing-container landing-contact-strip">
          <article>
            <span><Icon name="pin" size={24} /></span>
            <div>
              <b>कार्यालय पत्ता</b>
              <p>तालुका ग्रामपंचायत कार्यालय, दौंड, जि. पुणे, महाराष्ट्र - 412203</p>
            </div>
          </article>
          <article>
            <span><Icon name="phone" size={24} /></span>
            <div>
              <b>दूरध्वनी</b>
              <a href="tel:+919923466265">+91 99234 66265</a>
            </div>
          </article>
          <article>
            <span><Icon name="mail" size={24} /></span>
            <div>
              <b>ईमेल</b>
              <a href="mailto:taluka.daund@maharashtra.gov.in">taluka.daund@maharashtra.gov.in</a>
            </div>
          </article>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-container landing-footer-grid">
          <div>
            <h3>डिजिटल ग्रामपंचायत पोर्टल</h3>
            <p>दौंड तालुका, जि. पुणे, महाराष्ट्र</p>
          </div>
          <nav>
            <a href="#top">मुख्यपृष्ठ</a>
            <a href="#gp-search">ग्रामपंचायत शोधा</a>
            <Link to="/login">प्रशासक लॉगिन</Link>
            <Link to="/legal/terms">अटी व शर्ती</Link>
            <Link to="/legal/privacy">गोपनीयता</Link>
            <Link to="/legal/refund">परतावा धोरण</Link>
            <Link to="/legal/data-deletion">खाते हटवणे</Link>
            <Link to="/legal/accessibility">सुलभता</Link>
          </nav>
          <p>© {new Date().getFullYear()} डिजिटल ग्रामपंचायत पोर्टल. सर्व हक्क राखीव.</p>
        </div>
      </footer>
    </div>
  );
}
